import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiDroplet, FiLeaf, FiEye, FiSun } = FiIcons;

const Experience = () => {
  const [ref, inView] = useInView({
    threshold: 0.2,
    triggerOnce: true
  });

  const experiences = [
    {
      icon: FiDroplet,
      title: "The Healing Spring",
      description: "Cool, fresh water fed directly by the rainforest springs. Chemical-free natural pool that rejuvenates your soul.",
      image: "https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-blue-500 to-cyan-400"
    },
    {
      icon: FiLeaf,
      title: "The Dellawa River",
      description: "Private access to the Dellawa Ela. Kayak through the mist or bathe in the crystal clear waters of this pristine mountain stream.",
      image: "https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-emerald-500 to-teal-400"
    },
    {
      icon: FiEye,
      title: "The Wildlife",
      description: "Share your morning coffee with Peacocks and Kabaragoyas. You are the guest in their home, not the other way around.",
      image: "https://images.unsplash.com/photo-1574263867128-a3d5c1b1deae?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-purple-500 to-pink-400"
    },
    {
      icon: FiSun,
      title: "The Serenity",
      description: "Wake up to the sounds of the rainforest. No traffic, no crowds - just pure, unadulterated nature at its finest.",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
      color: "from-orange-500 to-yellow-400"
    }
  ];

  return (
    <section id="experience" className="relative py-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23059669' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />
      </div>

      <div ref={ref} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.h2
            className="text-4xl md:text-5xl font-bold text-white mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Why Choose
            <span className="gradient-primary bg-clip-text text-transparent ml-3">
              Villa Springwater?
            </span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-300 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            We don't just offer accommodation - we curate sensory experiences that connect you with the raw beauty of the Sinharaja rainforest ecosystem.
          </motion.p>
        </motion.div>

        {/* Experience Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {experiences.map((experience, index) => (
            <motion.div
              key={index}
              className="group relative overflow-hidden rounded-3xl"
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 * index }}
              whileHover={{ scale: 1.02 }}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={experience.image}
                  alt={experience.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
              </div>

              {/* Content */}
              <div className="relative z-10 p-8 h-80 flex flex-col justify-end">
                {/* Icon */}
                <motion.div
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-r ${experience.color} mb-4 shadow-lg`}
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.6 }}
                >
                  <SafeIcon icon={experience.icon} className="text-white text-2xl" />
                </motion.div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-emerald-300 transition-colors duration-300">
                  {experience.title}
                </h3>

                {/* Description */}
                <p className="text-gray-300 leading-relaxed">
                  {experience.description}
                </p>

                {/* Glass Effect Overlay */}
                <div className="absolute inset-0 glass-morphism opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
        >
          <div className="glass-dark rounded-2xl p-6 inline-block">
            <p className="text-gray-300 text-lg mb-4">
              Ready to experience nature's luxury at budget prices?
            </p>
            <motion.button
              onClick={() => {
                const message = encodeURIComponent("I'd love to experience Villa Springwater! Can you tell me more about availability?");
                window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
              }}
              className="gradient-primary text-white px-8 py-3 rounded-xl font-semibold hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Start Your Journey
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;